import { Component, ErrorInfo, ReactNode } from 'react';
import { error as logError, debug } from '@/lib/logger';

interface Props {
  children: ReactNode;
  onRetry?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  retryCount: number;
  isRetrying: boolean;
}

/**
 * Error boundary for Convex connection failures
 * Handles 503 Service Unavailable and other runtime errors
 * Implements exponential backoff retry strategy
 */
export class ConvexErrorBoundary extends Component<Props, State> {
  private retryTimeoutId: NodeJS.Timeout | null = null;
  private readonly MAX_RETRIES = 5;
  private readonly BASE_DELAY = 1000; // 1 second

  public state: State = {
    hasError: false,
    error: null,
    retryCount: 0,
    isRetrying: false,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logError('Convex Error Boundary caught an error:', error, { componentStack: errorInfo.componentStack });

    // Check if this is a 503 or connection error
    const is503Error = error.message.includes('503') ||
                       error.message.includes('Service') ||
                       error.message.includes('unavailable');

    if (is503Error && this.state.retryCount < this.MAX_RETRIES) {
      this.scheduleRetry();
    }
  }

  public componentWillUnmount() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
  }

  private scheduleRetry = () => {
    this.setState({ isRetrying: true });

    // Exponential backoff: 1s, 2s, 4s, 8s, 16s
    const delay = this.BASE_DELAY * Math.pow(2, this.state.retryCount);

    debug(`Scheduling retry ${this.state.retryCount + 1}/${this.MAX_RETRIES} in ${delay}ms`);

    this.retryTimeoutId = setTimeout(() => {
      this.handleRetry();
    }, delay);
  };

  private handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      retryCount: prevState.retryCount + 1,
      isRetrying: false,
    }));

    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  private handleManualRetry = () => {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
    this.handleRetry();
  };

  public render() {
    if (this.state.hasError) {
      const { error, retryCount, isRetrying } = this.state;
      const hasReachedMaxRetries = retryCount >= this.MAX_RETRIES;

      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '20px',
          backgroundColor: '#17171A',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}>
          <div style={{
            maxWidth: '600px',
            width: '100%',
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '32px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}>
            <div style={{
              fontSize: '48px',
              marginBottom: '16px',
              textAlign: 'center',
            }}>
              ⚠️
            </div>

            <h1 style={{
              fontSize: '24px',
              fontWeight: '600',
              marginBottom: '16px',
              textAlign: 'center',
              // Vault Black, NOT brand cream. This card is white, so #F5F2EC
              // rendered the heading at roughly 1.05:1 -- effectively invisible.
              // The heading was cream because the surrounding page is #17171A;
              // the card background was never switched to match.
              color: '#111113',
            }}>
              Service Temporarily Unavailable
            </h1>

            <p style={{
              fontSize: '16px',
              lineHeight: '1.5',
              marginBottom: '24px',
              textAlign: 'center',
              color: '#A6AAB0',
            }}>
              We're having trouble connecting to our backend services. This is usually temporary.
            </p>

            {isRetrying && (
              <div style={{
                padding: '12px',
                backgroundColor: '#151B2B',
                border: '1px solid #2253C9',
                borderRadius: '4px',
                marginBottom: '16px',
                textAlign: 'center',
              }}>
                <p style={{ margin: 0, color: '#A6C4FF' }}>
                  Automatically retrying... (Attempt {retryCount + 1}/{this.MAX_RETRIES})
                </p>
              </div>
            )}

            {hasReachedMaxRetries && (
              <div style={{
                padding: '12px',
                backgroundColor: '#241C08',
                border: '1px solid #F2B713',
                borderRadius: '4px',
                marginBottom: '16px',
              }}>
                <p style={{ margin: 0, color: '#F2B713', fontSize: '14px' }}>
                  <strong>Maximum retry attempts reached.</strong><br />
                  The service may be undergoing maintenance. Please try again later.
                </p>
              </div>
            )}

            <button
              onClick={this.handleManualRetry}
              disabled={isRetrying}
              style={{
                width: '100%',
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: '500',
                color: 'white',
                backgroundColor: isRetrying ? '#A6AAB0' : '#F2B713',
                border: 'none',
                borderRadius: '4px',
                cursor: isRetrying ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.2s',
              }}
              onMouseOver={(e) => {
                if (!isRetrying) {
                  e.currentTarget.style.backgroundColor = '#D19E0B';
                }
              }}
              onMouseOut={(e) => {
                if (!isRetrying) {
                  e.currentTarget.style.backgroundColor = '#F2B713';
                }
              }}
            >
              {isRetrying ? 'Retrying...' : 'Try Again Now'}
            </button>

            {error && (
              <details style={{
                marginTop: '24px',
                fontSize: '14px',
                color: '#A6AAB0',
              }}>
                <summary style={{ cursor: 'pointer', marginBottom: '8px' }}>
                  Technical Details
                </summary>
                <pre style={{
                  backgroundColor: '#17171A',
                  padding: '12px',
                  borderRadius: '4px',
                  overflow: 'auto',
                  fontSize: '12px',
                }}>
                  {error.message}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
